import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Popover, useSidebar } from '@cloudflare/kumo';
import {
  AppWindow, CaretDown, CaretRight, Info, List, Plus, Question,
  SquaresFour,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useNotifications } from '../hooks/useNotifications';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { ProfileMenu } from './ProfileMenu';
import { AiAssistant, useAiStatus } from './AiAssistant';
import { JevPanel } from './jev/JevPanel';

/**
 * Barra superior do app, sobre componentes do Kumo (`Button`, `Popover`,
 * `Input`) e layout em Tailwind: menu (celular), seletores de organização e
 * projeto (com busca), Lumi, atalhos, sino de notificações, ferramentas e
 * perfil.
 */

/** Popover do Kumo com gatilho e conteúdo simples (substitui o Popover do Cloudscape). */
function Pop({ trigger, children, width = 'w-80' }: { trigger: ReactNode; children: ReactNode; width?: string }) {
  return (
    <Popover.Root>
      <Popover.Trigger render={trigger as React.ReactElement} />
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={8}>
          <Popover.Popup className={`${width} max-w-[calc(100vw-1rem)] p-3`}>{children}</Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

const itemCls = 'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-kumo-default hover:bg-kumo-tint';

interface OrgTenant {
  id: string;
  name: string;
  slug?: string;
  active?: boolean;
}

export function TopHeader() {
  const { user } = useAuth();
  const { selectedTenant, organizations, projects, selectedProject, setSelectedProjectId, can } = useTenant();
  const navigate = useNavigate();
  const aiStatus = useAiStatus();
  const useOldAssistant = (() => { try { return localStorage.getItem('ingrave:assistente') === 'antigo'; } catch { return false; } })();

  const [allTenants, setAllTenants] = useState<Array<{ id: string; name: string; slug?: string; active?: boolean }>>([]);
  const [switching, setSwitching] = useState(false);

  const { unreadCount } = useNotifications();

  const isSuperAdmin = user?.role === 'SUPERADMIN';
  // Membros de mais de uma organização também trocam pela barra superior
  const location = useLocation();
  const onPlatform = isSuperAdmin && location.pathname.startsWith('/super-admin');
  const currentOrgName = selectedTenant?.name || 'Organização';
  const selectedTenantId = localStorage.getItem('selected_tenant_id') || selectedTenant?.id;

  useEffect(() => {
    if (isSuperAdmin) loadTenants();
  }, [isSuperAdmin]);

  const authenticatedFetch = useAuthenticatedFetch();

  const loadTenants = async () => {
    try {
      const response = await authenticatedFetch('/api/user-tenants');
      if (response.ok) {
        const data = await response.json();
        setAllTenants(data.tenants || []);
      }
    } catch {
      // silencioso
    }
  };

  const handleOrgSwitch = async (tenantId: string) => {
    if (switching || tenantId === selectedTenantId) return;
    try {
      setSwitching(true);
      const response = await authenticatedFetch('/api/user-tenants/switch', {
        method: 'POST',
        body: JSON.stringify({ tenantId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          localStorage.setItem('auth_token', data.token);
          localStorage.setItem('selected_tenant_id', tenantId);
        }
        // Cada organização tem os seus projetos: começa pelo padrão da nova
        localStorage.removeItem('selected_project_id');
        window.location.reload();
      }
    } catch {
      setSwitching(false);
    }
  };

  // Troca de projeto: as páginas remontam com os dados do novo projeto. Telas
  // de um registro específico voltam para a lista (o registro é do projeto antigo).
  const handleProjectSwitch = (projectId: string) => {
    if (projectId === selectedProject?.id) return;
    setSelectedProjectId(projectId);
    if (/^\/campanhas\/interativa\/.+/.test(location.pathname)) navigate('/campanhas/interativa');
  };

  const { toggleSidebar } = useSidebar();

  return (
    <header className="flex h-14 nav-topbar items-center gap-3 border-b border-kumo-line bg-kumo-base px-3 sm:px-4">
      <Button
        variant="ghost"
        shape="square"
        icon={<List />}
        aria-label="Menu"
        onClick={toggleSidebar}
        className="shrink-0"
      />

      {/* ── Contexto: ORGANIZAÇÃO → PROJETO ── */}
      <div className="flex min-w-0 items-center gap-1.5 text-sm">
        {onPlatform ? (
          // Na área Plataforma tudo vale para todas as organizações: sem seletor
          <span className="text-xs font-semibold uppercase tracking-wide text-kumo-subtle">Plataforma</span>
        ) : (
          <>
            <span className="hidden text-xs font-semibold uppercase tracking-wide text-kumo-subtle sm:inline">Organização</span>
            <Pop
              trigger={
                <Button variant="ghost" size="sm" aria-label="Trocar de organização" data-testid="topbar-org-switcher">
                  <span className="max-w-[10rem] truncate">{switching ? 'Trocando...' : currentOrgName}</span>
                  <CaretDown size={12} />
                </Button>
              }
            >
              <OrgSwitcherContent
                tenants={isSuperAdmin ? allTenants : organizations}
                isSuperAdmin={isSuperAdmin}
                selectedTenantId={selectedTenantId ?? undefined}
                onSelect={handleOrgSwitch}
                onDetails={() => navigate('/organizacao')}
                onPlatform={() => navigate('/super-admin')}
              />
            </Pop>

            <CaretRight size={12} className="shrink-0 text-kumo-subtle" />

            <span className="hidden text-xs font-semibold uppercase tracking-wide text-kumo-subtle sm:inline">Projeto</span>
            <Pop
              trigger={
                <Button variant="ghost" size="sm" aria-label="Trocar de projeto" data-testid="topbar-project-switcher">
                  <span className="max-w-[10rem] truncate">{selectedProject?.name || 'Principal'}</span>
                  <CaretDown size={12} />
                </Button>
              }
            >
              <ProjectSwitcherContent
                projects={projects}
                selectedProjectId={selectedProject?.id}
                canCreate={can('projects:manage')}
                onSelect={handleProjectSwitch}
                onNew={() => navigate('/projetos?novo=1')}
                onAll={() => navigate('/projetos')}
              />
            </Pop>
          </>
        )}
      </div>

      {/* ── Ações à direita ── */}
      <div className="ml-auto flex items-center gap-1">
        {/* Lumi, assistente de IA central (só com provedor de IA ativo). O assistente antigo
            volta com localStorage "ingrave:assistente" = "antigo" (chave de reversão da J4). */}
        {useOldAssistant || aiStatus?.jev === false ? <AiAssistant /> : aiStatus?.available && <JevPanel />}

        <Button variant="ghost" icon={<Question />} onClick={() => navigate('/suporte')}>
          <span className="hidden sm:inline">Suporte</span>
        </Button>

        <ProfileMenu unreadCount={unreadCount} />
      </div>
    </header>
  );
}

/* ── Seletores de organização e projeto ─────────────────────── */

function SearchList<T extends { id: string; name: string }>({
  items, selectedId, placeholder, label, meta, onSelect, children,
}: {
  items: T[];
  selectedId?: string;
  placeholder: string;
  label: string;
  meta?: (item: T) => string;
  onSelect: (id: string) => void;
  children?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const visible = items.filter((i) => i.name.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <div className="flex flex-col gap-2">
      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
      />
      <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
        {visible.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${itemCls}${item.id === selectedId ? ' bg-kumo-tint font-medium' : ''}`}
            onClick={() => onSelect(item.id)}
          >
            <span className="truncate">{item.name}</span>
            <span className="shrink-0 text-xs text-kumo-subtle">{meta?.(item)}</span>
          </button>
        ))}
      </div>
      <div className="border-t border-kumo-line pt-1">{children}</div>
    </div>
  );
}

function OrgSwitcherContent({
  tenants, isSuperAdmin, selectedTenantId, onSelect, onDetails, onPlatform,
}: {
  tenants: OrgTenant[];
  isSuperAdmin: boolean;
  selectedTenantId?: string;
  onSelect: (tenantId: string) => void;
  onDetails: () => void;
  onPlatform: () => void;
}) {
  return (
    <SearchList
      items={tenants}
      selectedId={selectedTenantId}
      placeholder="Buscar organização..."
      label="Buscar organização"
      meta={(t) => (isSuperAdmin ? ((t as unknown as { plan?: { name?: string } }).plan?.name || 'Starter') : '')}
      onSelect={onSelect}
    >
      <button type="button" className={itemCls} onClick={onDetails}>
        <span className="flex items-center gap-2"><Info size={16} />Detalhes desta organização</span>
      </button>
      {isSuperAdmin && (
        <button type="button" className={itemCls} onClick={onPlatform}>
          <span className="flex items-center gap-2"><SquaresFour size={16} />Gerenciar todas as organizações</span>
        </button>
      )}
    </SearchList>
  );
}

function ProjectSwitcherContent({
  projects, selectedProjectId, canCreate, onSelect, onNew, onAll,
}: {
  projects: Array<{ id: string; name: string; isDefault?: boolean }>;
  selectedProjectId?: string;
  canCreate: boolean;
  onSelect: (projectId: string) => void;
  onNew: () => void;
  onAll: () => void;
}) {
  return (
    <SearchList
      items={projects}
      selectedId={selectedProjectId}
      placeholder="Buscar projeto..."
      label="Buscar projeto"
      meta={(p) => (projects.find((x) => x.id === p.id)?.isDefault ? 'Projeto padrão' : '')}
      onSelect={onSelect}
    >
      {canCreate && (
        <button type="button" className={itemCls} onClick={onNew}>
          <span className="flex items-center gap-2"><Plus size={16} />Novo projeto</span>
        </button>
      )}
      <button type="button" className={itemCls} onClick={onAll}>
        <span className="flex items-center gap-2"><AppWindow size={16} />Ver todos os projetos</span>
      </button>
    </SearchList>
  );
}
